import { z } from "zod";

export const MAX_PAGES = 10;
export const MAX_INPUT_BYTES = 25 * 1024 * 1024;
export const MAX_INPUT_PIXELS = 40_000_000;
export const MAX_PACKAGE_BYTES = 8 * 1024 * 1024;
export const PACKAGE_VERSION = 1 as const;

export const pointSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
});

export const cornersSchema = z.tuple([
  pointSchema,
  pointSchema,
  pointSchema,
  pointSchema,
]);
export type Point = z.infer<typeof pointSchema>;
export type Corners = z.infer<typeof cornersSchema>;

export const processingModeSchema = z.enum(["original", "clean", "extract"]);
export type ProcessingMode = z.infer<typeof processingModeSchema>;

export const processingSettingsSchema = z.object({
  mode: processingModeSchema,
  cleanup: z.number().min(0).max(1),
  inkContrast: z.number().min(0).max(1),
  paperBrightness: z.number().min(0).max(1),
  shadowReduction: z.number().min(0).max(1),
  extractionStrength: z.number().min(0).max(1),
});
export type ProcessingSettings = z.infer<typeof processingSettingsSchema>;

export const defaultProcessingSettings: ProcessingSettings = {
  mode: "clean",
  cleanup: 0.55,
  inkContrast: 0.5,
  paperBrightness: 0.5,
  shadowReduction: 0.55,
  extractionStrength: 0.6,
};

export const defaultCorners: Corners = [
  { x: 0.035, y: 0.035 },
  { x: 0.965, y: 0.035 },
  { x: 0.965, y: 0.965 },
  { x: 0.035, y: 0.965 },
];

export const stationerySchema = z.enum([
  "warm-ivory",
  "clean-white",
  "soft-grey",
  "air-mail",
]);
export type StationeryId = z.infer<typeof stationerySchema>;

export const letterPageManifestSchema = z.object({
  id: z.string().min(1).max(80),
  file: z.string().regex(/^pages\/[a-zA-Z0-9-]+\.(webp|jpe?g)$/),
  width: z.number().int().positive().max(10_000),
  height: z.number().int().positive().max(10_000),
  mode: processingModeSchema,
  mimeType: z.enum(["image/webp", "image/jpeg"]),
});

export const letterManifestSchema = z.object({
  version: z.literal(PACKAGE_VERSION),
  letter: z.object({
    senderName: z.string().max(80),
    recipientName: z.string().max(80),
    title: z.string().max(120),
    displayDate: z.string().max(80),
    coverNote: z.string().max(240),
    createdAt: z.iso.datetime(),
    stationeryId: stationerySchema,
    envelopeId: z.literal("classic-ivory"),
    foldStyle: z.literal("tri-fold"),
    readingDirection: z.literal("vertical"),
    replyToLetterId: z.uuid().nullable(),
    readerToken: z.string().min(32).max(128),
  }),
  pages: z.array(letterPageManifestSchema).min(1).max(MAX_PAGES),
});
export type LetterManifest = z.infer<typeof letterManifestSchema>;

export const letterStatusSchema = z.enum([
  "pending",
  "active",
  "revoked",
  "expired",
  "not_found",
]);
export type LetterStatus = z.infer<typeof letterStatusSchema>;

export const acknowledgementSchema = z.enum([
  "received_with_love",
  "read_thoughtfully",
  "keeping_this",
]);
export type Acknowledgement = z.infer<typeof acknowledgementSchema>;

export const eventNameSchema = z.enum([
  "creation_started",
  "page_captured",
  "page_processing_completed",
  "letter_previewed",
  "letter_uploaded",
  "share_initiated",
  "letter_opened",
  "letter_downloaded",
  "acknowledgement_sent",
  "reply_started",
  "reply_completed",
  "processing_failure",
  "upload_failure",
  "decryption_failure",
]);
export type EventName = z.infer<typeof eventNameSchema>;

export const createLetterRequestSchema = z.object({
  letterId: z.uuid(),
  packageVersion: z.literal(PACKAGE_VERSION),
  expectedSizeBytes: z.number().int().positive().max(MAX_PACKAGE_BYTES),
  expiresInDays: z.union([z.literal(7), z.literal(30), z.literal(90)]),
  creatorTokenHash: z
    .string()
    .length(64)
    .regex(/^[a-f0-9]+$/),
  readerTokenHash: z
    .string()
    .length(64)
    .regex(/^[a-f0-9]+$/),
  replyToLetterId: z.uuid().nullable(),
  replyReaderToken: z.string().min(32).max(128).nullable(),
  challengeToken: z.string().max(4096).nullable(),
});

export const collectEventRequestSchema = z.object({
  eventId: z.uuid(),
  eventName: eventNameSchema,
  letterId: z.uuid().nullable(),
  draftSessionId: z.uuid().nullable(),
  readerToken: z.string().min(32).max(128).nullable(),
  schemaVersion: z.literal(1),
  properties: z
    .record(z.string(), z.union([z.string().max(120), z.number(), z.boolean()]))
    .refine((value) => Object.keys(value).length <= 8),
});

export function assertSafePackagePath(path: string): void {
  if (path.includes("..") || path.startsWith("/") || path.includes("\\")) {
    throw new Error("The letter package contains an unsafe file path.");
  }
}
